import React from 'react';
import { Version } from '../types';

interface VersionsTableProps {
  versions: Version[];
  isProcessing: boolean;
  onDownloadPDF: (version: Version) => void;
  onApprove: (versionId: string) => void;
  onReject: (version: Version) => void;
}

const statusColors: Record<string, string> = {
  submitted: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const VersionsTable: React.FC<VersionsTableProps> = ({
  versions,
  isProcessing,
  onDownloadPDF,
  onApprove,
  onReject,
}) => {
  if (versions.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Versions</h2>
        </div>
        <div className="px-6 py-12 text-center text-gray-500">
          No version history available. Versions are created when a book is submitted for review.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Versions ({versions.length})</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Version</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created At</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comment</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reviewed By</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {versions.map((version) => (
              <tr key={version._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                  v{version.versionNumber}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {new Date(version.createdAt).toLocaleString()}
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[version.status] || ''}`}>
                    {version.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate" title={version.comment || ''}>
                  {version.comment || '-'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {version.reviewedBy?.name || version.reviewedBy?.email || '-'}
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => onDownloadPDF(version)}
                      className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      Download PDF
                    </button>
                    {version.status === 'submitted' && (
                      <>
                        <button
                          onClick={() => onApprove(version._id)}
                          disabled={isProcessing}
                          className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                        >
                          Accept
                        </button>
                        <button
                          onClick={() => onReject(version)}
                          disabled={isProcessing}
                          className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VersionsTable;
